/**
 * Result Pattern Implementation
 * A discriminated union for explicit success/failure handling without exceptions
 */

import { AppError } from './errors.interface';

/**
 * Success result containing data
 */
export interface Success<T> {
  success: true;
  data: T;
}

/**
 * Failure result containing error
 */
export interface Failure<E = AppError> {
  success: false;
  error: E;
}

/**
 * Result type - discriminated union of Success or Failure
 * Use this as the return type for all API operations
 * 
 * @example
 * const result: Result<User> = await userApi.getUser(id);
 * if (result.success) {
 *   console.log(result.data.name);
 * } else {
 *   console.error(result.error.message);
 * }
 */
export type Result<T, E = AppError> = Success<T> | Failure<E>;

/**
 * Helper to create a success result
 */
export function success<T>(data: T): Success<T> {
  return { success: true, data };
}

/**
 * Helper to create a failure result
 */
export function failure<E = AppError>(error: E): Failure<E> {
  return { success: false, error };
}

/**
 * Type guard to check if result is successful
 */
export function isSuccess<T, E>(result: Result<T, E>): result is Success<T> {
  return result.success === true;
}

/**
 * Type guard to check if result is a failure
 */
export function isFailure<T, E>(result: Result<T, E>): result is Failure<E> {
  return result.success === false;
}
